<template>
    <div id="diagram-container" :class="{ 'showing-diff': showAsDiff }">
        <div class="bpmn-js to-diff" ref="bpmnJsToDiffElement">
            <div v-if="showAsDiff" class="version-header">
                <div class="filler" />
                <Tag :value="toName" />
                <div class="filler" />
            </div>
        </div>
        <div
            v-if="showAsDiff"
            class="bpmn-js from-diff"
            ref="bpmnJsFromDiffElement"
        >
            <div class="version-header">
                <div class="filler" />
                <Tag :value="fromName" />
                <div class="filler" />
            </div>
        </div>
        <Panel
            header="Changes"
            :collapsed="true"
            toggleable
            style="z-index: 200; position: absolute; bottom: 0; width: 100%"
        >
            <DataTable
                :value="changes"
                ref="dataTable"
                scrollable
                scrollHeight="flex"
                stripedRows
                rowHover
                sortField="type"
                :sortOrder="1"
                style="max-height: 350px"
            >
                <Column header="Element" field="element.name" sortable>
                    <template #body="{ data }">
                        <span
                            class="bpmn-element"
                            :data-element-id="data.element.id"
                        >
                            {{ data.element.name || data.element.id }}
                        </span>
                    </template>
                </Column>
                <Column header="Type" sortable sortField="element.$type">
                    <template #body="slotProps">
                        {{ slotProps.data.element.$type?.replace("bpmn:", "") }}
                    </template>
                </Column>
                <Column sortable sortField="type">
                    <template #body="slotProps">
                        <Tag
                            :value="slotProps.data.type"
                            :severity="
                                typeToSeverity[
                                    slotProps.data.type as ChangeType
                                ]
                            "
                        />
                    </template>
                </Column>
            </DataTable>
        </Panel>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, watch, ref, onMounted } from "vue";
import DataTable from "primevue/datatable";
import { useBpmnManager } from "@/composables/bpmn-manager/index";
import { useBpmnLayerManager } from "@/composables/bpmn-manager/layer";
import {
    type ChangeType,
    useBpmnDiffManager,
} from "@/composables/bpmn-manager/diff";
import { type NavigatedViewer } from "@/composables/bpmn-manager/helpers";

const props = withDefaults(
    defineProps<{
        from: string;
        fromName?: string;
        to: string;
        toName?: string;
        showAsDiff?: boolean;
    }>(),
    {
        fromName: "Previous version",
        toName: "Selected version",
        showAsDiff: false,
    }
);

const layerManagerFeatures = {
    roleSelect: {
        enabled: ref(false),
    },
    navigationalElements: {
        enabled: ref(false),
    },
    taskWindowElements: {
        enabled: ref(false),
    },
    dotForMissingTasks: {
        enabled: ref(false),
    },
    highlight: {
        enabled: ref("onAll"),
    },
};

const bpmnJsFromDiffElement = ref<HTMLElement>();
const bpmnJsToDiffElement = ref<HTMLElement>();
const dataTable = ref<InstanceType<typeof DataTable>>();

const fromBpmnManager = useBpmnManager<NavigatedViewer>();
const toBpmnManager = useBpmnManager<NavigatedViewer>();
const toLayerManager = useBpmnLayerManager(toBpmnManager, layerManagerFeatures);
const fromLayerManager = useBpmnLayerManager(
    fromBpmnManager,
    layerManagerFeatures
);

defineExpose({
    getSvg: toBpmnManager.getSvg,
});

const { run, changes, markChanges, setXml, syncViewers, typeToSeverity } =
    useBpmnDiffManager(fromBpmnManager, toBpmnManager);

onMounted(() => {
    toBpmnManager.initViewer(bpmnJsToDiffElement.value!, {
        isScrollingEnabled: true,
    });

    fromBpmnManager.initViewer(bpmnJsFromDiffElement.value, {
        isScrollingEnabled: true,
    });

    syncViewers();
});

/**
 * Attach the from viewer to the DOM.
 */
watch(
    () => props.showAsDiff,
    async () => {
        if (props.showAsDiff) {
            nextTick(() => {
                fromBpmnManager.attachTo(bpmnJsFromDiffElement.value!);
                // Trigger the `canvas.viewbox.changed` so that the viewers are synced.
                toBpmnManager.eventBus?.fire("canvas.viewbox.changed", {
                    viewbox: toBpmnManager.canvas?.viewbox(),
                });
            });
        } else {
            fromBpmnManager.bpmnJs.value?.detach();
        }
    }
);

watch(
    changes,
    () => {
        dataTable.value?.$el
            .querySelectorAll(".p-datatable-tbody > tr")
            .forEach((row: HTMLElement) => {
                const elmId =
                    row.querySelector<HTMLElement>(".bpmn-element")?.dataset
                        ?.elementId;
                if (!elmId) return;

                row.addEventListener("pointerenter", () => {
                    highlightElement(elmId);
                });
                row.addEventListener("pointerleave", () => {
                    removeHighlight(elmId);
                });
            });
    },
    { flush: "post" }
);

const bothInitialized = computed(() => {
    return !!(
        fromBpmnManager.initialized?.value && toBpmnManager.initialized?.value
    );
});

watch(
    [() => props.from, () => props.to, bothInitialized],
    async ([newFrom, newTo, newBothInitialized]) => {
        if (newFrom && newBothInitialized) {
            await setXml("from", newFrom);
            await setXml("to", newTo);
            run();
            markChanges();
        }
    },
    { immediate: true }
);

watch(
    [
        () => fromBpmnManager.hoveredBpmnElement.value,
        () => toBpmnManager.hoveredBpmnElement.value,
    ],
    ([newFromElement, newToElement], [oldFromElement, oldToElement]) => {
        if (oldFromElement) {
            removeHighlight(oldFromElement.id);
        }
        if (oldToElement) {
            removeHighlight(oldToElement.id);
        }
        const element = newFromElement || newToElement;
        if (element) {
            highlightElement(element.id);
        }
    }
);

const highlightElement = (elementId: string) => {
    if (elementId) {
        fromLayerManager.highlight(elementId);
        toLayerManager.highlight(elementId);
    }
};

const removeHighlight = (elementId: string) => {
    if (elementId) {
        fromLayerManager.clearHighlight(elementId);
        toLayerManager.clearHighlight(elementId);
    }
};
</script>
<style lang="scss" scoped>
#diagram-container {
    width: 100%;
    position: relative;
    display: grid;
    // Reserve some space for the collapsed properties panel
    grid-template-rows: auto 54px;

    &.showing-diff {
        grid-template-rows: auto auto 54px;

        .bpmn-js {
            border-bottom-width: 5px;
            border-bottom-style: dashed;

            &.from-diff {
                display: flex;
                // Using a transparent border to make sure the diagrams have exactly
                // the same size.
                border-bottom-color: transparent;
            }
        }
    }

    .bpmn-js {
        position: relative;
        display: flex;

        &.from-diff {
            display: none;

            .version-header .p-tag {
                background: var(--stekz-primary-background);
                color: var(--text-color);
            }
        }

        &.to-diff {
            .version-header {
                bottom: 0;

                .p-tag {
                    background: var(--stekz-primary-color);
                    color: var(--stekz-white);
                }
            }
        }

        .version-header {
            position: absolute;
            width: 100%;
            z-index: 1;

            > .p-tag {
                margin: 8px;
            }

            > .filler {
                flex: 1;
            }
        }

        :deep(.djs-element) {
            &.hover {
                .djs-outline {
                    stroke: var(--stekz-biz-lighest-color);
                }
            }
            &.selected {
                .djs-outline {
                    stroke: var(--stekz-biz-darkest-color);
                    stroke-width: 2px;
                }
                /* Selected task (rect), gateway (polygon) or start/end/error (circle) */
                .djs-visual {
                    rect,
                    circle,
                    polygon {
                        fill: var(--stekz-biz-lightest-color) !important;
                    }
                }
            }
            &.diff-added {
                background-color: lightgreen;
            }
            &.diff-changed {
                background-color: orange;
            }
            &.diff-removed {
                background-color: lightcoral;
            }
        }

        :deep(.djs-overlays) {
            .djs-overlay-files svg,
            .djs-overlay-dmn i,
            .djs-overlay-form i {
                color: var(--stekz-blue-darker);
                pointer-events: none;
            }
            .djs-overlay > span.default svg {
                opacity: 0.5;
            }
            .file-count {
                position: absolute;
                color: white;
                display: block;
                top: 0;
                left: 0;
                width: 1rem;
                text-align: center;
                font-size: 0.6rem;
                font-weight: bolder;
                line-height: 1.15rem;
            }
        }
    }
}
</style>
<style lang="css">
@import url("bpmn-js/dist/assets/diagram-js.css");
@import url("bpmn-js/dist/assets/bpmn-js.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css");
@import url("bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css");
@import url("@bpmn-io/properties-panel/assets/properties-panel.css");
@import url("bpmn-js-color-picker/colors/color-picker.css");
</style>
